/**
 * Ouverture de compte en libre-service — plateforme de test uniquement.
 *
 * C'est le point d'entrée du produit : un développeur doit pouvoir essayer sans
 * écrire à personne. Ce qui est demandé ici est le strict nécessaire pour
 * commencer. Les mentions légales — NIF, RCCM, siège — ne servent qu'à
 * facturer, et rien n'est facturé ici ; elles seront réclamées à l'ouverture du
 * compte de production, où elles ont un sens.
 */
import { useState, type FormEvent } from 'react';
import { api, stockerJeton } from '../api/client';
import { MessageErreur } from '../components/Ui';

export function Inscription({
  surInscription,
  surRetour,
}: {
  surInscription: () => void;
  surRetour: () => void;
}) {
  const [societe, setSociete] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [expediteur, setExpediteur] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);

  const motDePasseCourt = motDePasse.length > 0 && motDePasse.length < 10;
  const complet = societe.trim().length >= 2 && email.includes('@') && motDePasse.length >= 10;

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const resultat = await api.inscription({
        company_name: societe.trim(),
        email: email.trim(),
        password: motDePasse,
        full_name: nom.trim() || undefined,
        sender_id: expediteur.trim() || undefined,
      });
      // Le serveur ouvre la session dans la foulée : demander de se reconnecter
      // juste après s'être inscrit est une friction sans contrepartie.
      stockerJeton(resultat.token);
      surInscription();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="page-connexion">
      <div className="boite">
        <div className="marque">
          <img className="signe" src="/favicon.svg" alt="" width="44" height="44" />
          <div className="nom">INFRATEX SMPP</div>
          <div className="produit">Console — plateforme de test</div>
        </div>

        <div className="carte">
          <div className="corps">
            <h2 style={{ marginBottom: 6, fontSize: 18 }}>Créer un compte de test</h2>
            <p style={{ marginTop: 0, marginBottom: 16, fontSize: 13, color: 'var(--muet)' }}>
              Gratuit et immédiat. Aucun message n’atteint un réseau GSM, rien n’est facturé.
            </p>
            <MessageErreur erreur={erreur} />

            <form onSubmit={soumettre}>
              <label className="champ" style={{ marginBottom: 14 }}>
                <span>Raison sociale</span>
                <input
                  type="text"
                  value={societe}
                  onChange={(e) => setSociete(e.target.value)}
                  placeholder="Banque de Démonstration"
                  autoFocus
                />
              </label>

              <label className="champ" style={{ marginBottom: 14 }}>
                <span>Votre nom</span>
                <input
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Facultatif"
                />
              </label>

              <label className="champ" style={{ marginBottom: 14 }}>
                <span>Adresse électronique</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                />
              </label>

              <label className="champ" style={{ marginBottom: 14 }}>
                <span>Mot de passe</span>
                <input
                  type="password"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  autoComplete="new-password"
                />
                {motDePasseCourt && (
                  <div className="aide" style={{ color: 'var(--rouge)' }}>
                    Dix caractères au minimum.
                  </div>
                )}
              </label>

              <label className="champ" style={{ marginBottom: 14 }}>
                <span>Expéditeur souhaité</span>
                <input
                  type="text"
                  value={expediteur}
                  maxLength={11}
                  onChange={(e) => setExpediteur(e.target.value.toUpperCase())}
                  placeholder="MASOCIETE"
                />
                <div className="aide">
                  Onze caractères au maximum — contrainte SMPP, pas la nôtre. Validé d’office ici ;
                  en production, il devra être déclaré à l’opérateur.
                </div>
              </label>

              <button type="submit" className="accent" disabled={!complet || enCours}>
                {enCours ? 'Création…' : 'Créer mon compte de test'}
              </button>
            </form>

            <div className="aide">
              Vous avez déjà un compte ?{' '}
              <button type="button" className="lien" onClick={surRetour}>
                Se connecter
              </button>
              . Un compte de <strong>production</strong> ne s’ouvre pas ici : INFRATEX l’ouvre après
              votre intégration, sur un service distinct avec ses propres clés.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
